import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { appointmentAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { GlassCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Check, X, AlertTriangle } from 'lucide-react';

const Appointments = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => { loadAppointments(); }, []);

  const loadAppointments = async () => {
    try { const res = await appointmentAPI.getAppointments(); setAppointments(res.data); }
    catch (error) { toast.error('Failed to load appointments'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    try { await appointmentAPI.approve(id); toast.success('Appointment approved'); loadAppointments(); }
    catch { toast.error('Failed to approve appointment'); }
  };

  const handleReject = async (id) => {
    try { await appointmentAPI.reject(id, 'Not available'); toast.success('Appointment rejected'); loadAppointments(); }
    catch { toast.error('Failed to reject appointment'); }
  };

  const handleComplete = async (id) => {
    try { await appointmentAPI.complete(id); toast.success('Appointment marked as completed'); loadAppointments(); }
    catch { toast.error('Failed to complete appointment'); }
  };

  const filteredAppointments = appointments.filter((appt) => {
    if (activeTab === 'all') return true;
    return appt.status === activeTab;
  });

  const isAgent = user?.role === 'agent';

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div></div>);
  }

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{isAgent ? 'Manage Appointments' : 'My Appointments'}</h1>
        <p className="text-gray-500 mt-1">{isAgent ? 'View and manage appointment requests' : 'Track your viewing appointments'}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100/80">
          {['all', 'pending', 'approved', 'escalated', 'completed'].map((tab) => (
            <TabsTrigger key={tab} value={tab} className="capitalize">
              {tab}
              {tab !== 'all' && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                  {appointments.filter(a => a.status === tab).length}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredAppointments.length > 0 ? (
            <GlassCard className="overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-100">
                    <TableHead className="text-gray-500 font-semibold">Property</TableHead>
                    <TableHead className="text-gray-500 font-semibold">{isAgent ? 'Customer' : 'Agent'}</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Date & Time</TableHead>
                    <TableHead className="text-gray-500 font-semibold">Status</TableHead>
                    {isAgent && <TableHead className="text-gray-500 font-semibold">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAppointments.map((appt) => (
                    <TableRow key={appt.id} className="border-gray-50" data-testid={`appointment-${appt.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{appt.property?.name}</p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{appt.property?.location}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{isAgent ? appt.customer?.full_name : appt.agent?.full_name}</p>
                          <p className="text-sm text-gray-500">{isAgent ? appt.customer?.phone : appt.agent?.agent_code}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(appt.preferred_date)}
                          <Clock className="w-4 h-4 text-gray-400 ml-2" />
                          {appt.preferred_time}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={appt.status} />
                        {appt.status === 'escalated' && (
                          <div className="flex items-center gap-1 text-orange-500 text-xs mt-1">
                            <AlertTriangle className="w-3 h-3" />Escalated
                          </div>
                        )}
                      </TableCell>
                      {isAgent && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {(appt.status === 'pending' || appt.status === 'escalated') && (
                              <>
                                <Button size="sm" onClick={() => handleApprove(appt.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${appt.id}`}>
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleReject(appt.id)} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-${appt.id}`}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {appt.status === 'approved' && (
                              <Button size="sm" onClick={() => handleComplete(appt.id)} className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid={`complete-${appt.id}`}>
                                Mark Complete
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GlassCard>
          ) : (
            <GlassCard className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Appointments</h3>
              <p className="text-gray-400">{activeTab === 'all' ? 'You have no appointments yet' : `No ${activeTab} appointments`}</p>
            </GlassCard>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Appointments;
